import { z } from 'zod/v4';

import { RegionsEnumRuntype } from '../supplierAndStockLocation';

export enum PriceRadarStatusKind {
    InvalidSpecification = 'InvalidSpecification',
    SupplierApiError = 'SupplierApiError',
    Pending = 'Pending',
    NeedsRefreshForOffers = 'NeedsRefreshForOffers',
    OffersAvailable = 'OffersAvailable',
    NoOffersReceived = 'NoOffersReceived',
    PanelError = 'PanelError',
    MissingCredentials = 'MissingCredentials',
    InvalidCredentials = 'InvalidCredentials',
    ApiNotSetUp = 'ApiNotSetUp',
    CustomStackup = 'CustomStackup',
    StackratePricingBreaks = 'StackratePricingBreaks',
}

export enum PropertyErrorKindEnum {
    Empty = 'Empty',
    NotInSet = 'NotInSet',
    RequiredBySupplier = 'RequiredBySupplier',
    Plain = 'Plain',
    CustomLabel = 'CustomLabel',
    NotSupported = 'NotSupported',
    NotSupportedForInstantOffers = 'NotSupportedForInstantOffers',
    CombinationNotSupported = 'CombinationNotSupported',
    ConversionError = 'ConversionError',
    BelowMin = 'BelowMin',
    AboveMax = 'AboveMax',
}

export enum PropertyErrorCustomLabelEnum {
    WurthBlindViasError = 'WurthBlindViasError',
    WurthBuriedViasError = 'WurthBuriedViasError',
    WurthSurfaceFinishHalPbFree = 'WurthSurfaceFinishHalPbFree',
    SafePcbHardGoldError = 'SafePcbHardGoldError',
    MultiCbTGValueFR4Material = 'MultiCbTGValueFR4Material',
}

export type CustomLabelPropertyError = z.infer<typeof CustomLabelPropertyErrorRuntype>;
export const CustomLabelPropertyErrorRuntype = z.union([
    z.object({
        type: z.literal(PropertyErrorCustomLabelEnum.WurthBlindViasError),
    }),
    z.object({
        type: z.literal(PropertyErrorCustomLabelEnum.WurthBuriedViasError),
    }),
    z.object({
        type: z.literal(PropertyErrorCustomLabelEnum.WurthSurfaceFinishHalPbFree),
    }),
    z.object({
        type: z.literal(PropertyErrorCustomLabelEnum.SafePcbHardGoldError),
    }),
    z.object({
        type: z.literal(PropertyErrorCustomLabelEnum.MultiCbTGValueFR4Material),
        tg_value: z.string(),
    }),
]);

export type PropertyErrorKind = z.infer<typeof PropertyErrorKindRuntype>;
const PropertyErrorKindRuntype = z.union([
    z.object({ type: z.literal(PropertyErrorKindEnum.Empty) }),
    z.object({ type: z.literal(PropertyErrorKindEnum.NotInSet) }),
    z.object({ type: z.literal(PropertyErrorKindEnum.RequiredBySupplier) }),
    z.object({
        type: z.literal(PropertyErrorKindEnum.Plain),
        message: z.string(),
    }),
    z.object({
        type: z.literal(PropertyErrorKindEnum.CustomLabel),
        label: CustomLabelPropertyErrorRuntype,
    }),
    z.object({
        type: z.literal(PropertyErrorKindEnum.NotSupported),
        value: z.string().nullable().optional(),
        unit: z.string(),
    }),
    z.object({
        type: z.literal(PropertyErrorKindEnum.NotSupportedForInstantOffers),
        value: z.string().nullable().optional(),
        unit: z.string(),
    }),
    z.object({
        type: z.literal(PropertyErrorKindEnum.CombinationNotSupported),
        properties: z.array(z.string()),
    }),
    z.object({
        type: z.literal(PropertyErrorKindEnum.ConversionError),
        value: z.string().nullable().optional(),
        unit: z.string(),
    }),
    z.object({
        type: z.literal(PropertyErrorKindEnum.BelowMin),
        value: z.string(),
        min: z.string(),
        unit: z.string(),
    }),
    z.object({
        type: z.literal(PropertyErrorKindEnum.AboveMax),
        value: z.string(),
        max: z.string(),
        unit: z.string(),
    }),
]);

export type InvalidSpecificationError = z.infer<typeof InvalidSpecificationErrorRuntype>;
const InvalidSpecificationErrorRuntype = z.object({
    name: z.string(),
    error: z.string().optional().nullable(),
    kind: PropertyErrorKindRuntype.optional().nullable(),
});

const InvalidSpecificationRuntype = z.object({
    type: z.literal(PriceRadarStatusKind.InvalidSpecification),
    errors: z.array(InvalidSpecificationErrorRuntype),
});

const ApiErrorRuntype = z.object({
    type: z.literal(PriceRadarStatusKind.SupplierApiError),
});

const PendingRuntype = z.object({
    type: z.literal(PriceRadarStatusKind.Pending),
});

const NeedsRefreshForOffersRuntype = z.object({
    type: z.literal(PriceRadarStatusKind.NeedsRefreshForOffers),
});

const SuccessWithOffersStatusRuntype = z.object({
    type: z.literal(PriceRadarStatusKind.OffersAvailable),
});

const SuccessWithoutOffersStatusRuntype = z.object({
    type: z.literal(PriceRadarStatusKind.NoOffersReceived),
});

export enum PanelErrorKind {
    WidthExceedsDeliveryPanel = 'PcbWidthExceedsDeliveryPanel',
    HeightExceedsDeliveryPanel = 'PcbHeightExceedsDeliveryPanel',
    WidthBelowDeliveryPanelMinimum = 'PcbWidthBelowDeliveryPanelMinimum',
    HeightBelowDeliveryPanelMinimum = 'PcbHeightBelowDeliveryPanelMinimum',
    UnknownError = 'UnknownError',
}

const PanelErrorKindRuntype = z.object({
    type: z.enum(PanelErrorKind),
    message: z.string().optional(),
});

const PanelErrorStatusRuntype = z.object({
    type: z.literal(PriceRadarStatusKind.PanelError),
    error: PanelErrorKindRuntype,
});

const MissingCredentialsStatusRuntype = z.object({
    type: z.literal(PriceRadarStatusKind.MissingCredentials),
});

const InvalidCredentialsStatusRuntype = z.object({
    type: z.literal(PriceRadarStatusKind.InvalidCredentials),
});

export enum ApiNotSetupErrorKind {
    MissingGatemaCustomerId = 'MissingGatemaCustomerId',
}

const ApiNotSetUpRuntype = z.object({
    type: z.literal(PriceRadarStatusKind.ApiNotSetUp),
    error: z.object({
        type: z.enum(ApiNotSetupErrorKind),
    }),
});

const CustomStackupRuntype = z.object({
    type: z.literal(PriceRadarStatusKind.CustomStackup),
});

export type StackratePricingMessage = z.infer<typeof StackratePricingMessageRuntype>;
const StackratePricingMessageRuntype = z.object({
    name: z.string(),
    message: z.string(),
    fields: z.array(z.string()).optional().nullable(),
});

const StackratePricingBreaksRuntype = z.object({
    type: z.literal(PriceRadarStatusKind.StackratePricingBreaks),
    pricing_breaks: z.array(StackratePricingMessageRuntype),
});

export type CustomPartOfferStatuses = z.infer<typeof CustomPartOfferManufacturerStatusRuntype>;
const CustomPartOfferManufacturerStatusRuntype = z.union([
    PendingRuntype,
    SuccessWithOffersStatusRuntype,
    SuccessWithoutOffersStatusRuntype,
    NeedsRefreshForOffersRuntype,
    InvalidSpecificationRuntype,
    PanelErrorStatusRuntype,
    MissingCredentialsStatusRuntype,
    InvalidCredentialsStatusRuntype,
    ApiNotSetUpRuntype,
    ApiErrorRuntype,
    CustomStackupRuntype,
    StackratePricingBreaksRuntype,
]);

const SupplierOfferResultRuntype = z.object({
    kind: z.literal('Supplier'),
    supplier: z.object({
        id: z.string(),
        name: z.string(),
    }),
    stock_location: z.array(RegionsEnumRuntype),
    api: z.string().nullable().optional(),
    status: CustomPartOfferManufacturerStatusRuntype,
});

const ApiOfferResultRuntype = z.object({
    kind: z.literal('Api'),
    api: z.string(),
    status: CustomPartOfferManufacturerStatusRuntype,
});

export type DetailedCustomPartOfferData = z.infer<typeof DetailedCustomPartOfferResultResponseRuntype>;
const DetailedCustomPartOfferResultResponseRuntype = z.discriminatedUnion('kind', [
    SupplierOfferResultRuntype,
    ApiOfferResultRuntype,
]);

export enum CustomPartOfferResultType {
    NoSourcingScenarios = 'NoSourcingScenarios',
    AnalysisInProgress = 'AnalysisInProgress',
    NoSuppliersConfigured = 'NoSuppliersConfigured',
    NoDefinedQuantity = 'NoDefinedQuantity',
    NotApproved = 'NotApproved',
    Detailed = 'Detailed',
    Simple = 'Simple',
}

export type DetailedCustomPartOffers = z.infer<typeof CustomPartOfferResultArrayResponseRuntype>;
const CustomPartOfferResultArrayResponseRuntype = z.object({
    type: z.literal(CustomPartOfferResultType.Detailed),
    data: z.array(DetailedCustomPartOfferResultResponseRuntype),
    is_pending: z.boolean(),
});

export type SimpleOffersDTO = z.infer<typeof SimpleOfferResultResponseRuntype>;
const SimpleOfferResultResponseRuntype = z.object({
    type: z.literal(CustomPartOfferResultType.Simple),
    data: CustomPartOfferManufacturerStatusRuntype,
    is_pending: z.boolean(),
});

const NoSourcingScenariosRuntype = z.object({
    type: z.literal(CustomPartOfferResultType.NoSourcingScenarios),
});

const AnalysisInProgressRuntype = z.object({
    type: z.literal(CustomPartOfferResultType.AnalysisInProgress),
});

const NoSuppliersConfiguredRuntype = z.object({
    type: z.literal(CustomPartOfferResultType.NoSuppliersConfigured),
});

const NoDefinedQuantityRuntype = z.object({
    type: z.literal(CustomPartOfferResultType.NoDefinedQuantity),
});

export enum PcbWorkflowStep {
    FileManager = 'FileManager',
    DrillManager = 'DrillManager',
    Stackup = 'Stackup',
    Specification = 'Specification',
}

const NotApprovedRuntype = z.object({
    type: z.literal(CustomPartOfferResultType.NotApproved),
    missing_steps: z.array(z.enum(PcbWorkflowStep)),
});

export type CustomPartOfferResponse = z.infer<typeof CustomPartOfferResultResponseStatusRuntype>;
const CustomPartOfferResultResponseStatusRuntype = z.union([
    NoSourcingScenariosRuntype,
    AnalysisInProgressRuntype,
    NoSuppliersConfiguredRuntype,
    CustomPartOfferResultArrayResponseRuntype,
    SimpleOfferResultResponseRuntype,
    NotApprovedRuntype,
    NoDefinedQuantityRuntype,
]);

export const CustomPartOfferResultResponseRuntype = z.object({
    pcb_id: z.string(),
    status: CustomPartOfferResultResponseStatusRuntype,
});
